/**
 * useOrgStructure.ts
 * Hook to load and cache the organization's departments + roles from DB (SSOT).
 * Replaces in-memory defaultOrganization for all map/blueprint operations.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  getOrgStructure,
  type OrgDepartment,
  type OrgRole,
  type OrgStructure,
} from '../services/organizationStructureService';

const QUERY_KEY = 'org-structure';
const STALE_TIME = 10 * 60_000; // 10 min — structure rarely changes

export function useOrgStructure(organizationId: string | null | undefined) {
  const orgId = organizationId ?? '';

  const query = useQuery<OrgStructure>({
    queryKey: [QUERY_KEY, orgId],
    enabled: Boolean(orgId),
    staleTime: STALE_TIME,
    queryFn: () => getOrgStructure(orgId),
  });

  const departments = query.data?.departments ?? [];
  const roles = query.data?.roles ?? [];

  const rolesByDepartment = useMemo(() => {
    const map = new Map<string, OrgRole[]>();
    for (const role of roles) {
      const deptId = role.departmentId ?? '__none__';
      if (!map.has(deptId)) map.set(deptId, []);
      map.get(deptId)!.push(role);
    }
    return map;
  }, [roles]);

  /** Roles that belong to a given department */
  const getRolesForDepartment = (deptId: string): OrgRole[] => {
    return rolesByDepartment.get(deptId) ?? [];
  };

  const getDepartmentById = (deptId: string): OrgDepartment | undefined => {
    return departments.find(d => d.id === deptId);
  };

  const getRoleById = (roleId: string): OrgRole | undefined => {
    return roles.find(r => r.id === roleId);
  };

  /** Get department name for a role (resolved via role.departmentId) */
  const getDepartmentForRole = (roleId: string): OrgDepartment | undefined => {
    const role = roles.find(r => r.id === roleId);
    if (!role?.departmentId) return undefined;
    return departments.find(d => d.id === role.departmentId);
  };

  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY, orgId] });

  return {
    departments,
    roles,
    rolesByDepartment,
    getRolesForDepartment,
    getDepartmentById,
    getRoleById,
    getDepartmentForRole,
    isLoading: query.isPending,
    error: query.error,
    invalidate,
  };
}
